import { useAppDispatch } from 'src/hooks/store';
import { Attachment } from 'src/graphql/types/delivery';
import { GAttachmentQualityStatus } from 'src/graphql/types/generated';
import { showModal, ModalType } from '../../state/slices/modal';
import {
  uploadedImagesTitleWrapper,
  uploadedImagesTitle,
  imageCountContainer,
  imageCountStyle,
  noImagesLabelStyle,
  imageGalleryWrapper,
  thumbnailStyle,
  thumbnailWrapperStyle,
  qualityBadgeStyle,
  qualityBadgeHiddenStyle,
  imageListStyle,
  loaderStyles,
} from './styles';
import Skeleton from 'react-loading-skeleton';

// What the photo-quality classifier concluded, for staff eyes only. HIDDEN with
// the customer still able to see the photo means the backend is in shadow mode.
type QualityBadge = 'hidden' | 'flagged';

export const qualityBadge = (
  attachment: Pick<Attachment, 'qualityStatus' | 'customerVisible'>,
): QualityBadge | null => {
  if (attachment.qualityStatus !== GAttachmentQualityStatus.Hidden) return null;
  return attachment.customerVisible === false ? 'hidden' : 'flagged';
};

const QUALITY_BADGE_LABEL: Record<QualityBadge, string> = {
  hidden: 'Hidden from customer',
  flagged: 'Flagged low quality',
};

const ImageCount = ({
  count,
  idPrefix,
}: {
  count: number;
  idPrefix: string;
}): JSX.Element | null => {
  return count > 0 ? (
    <div id={`${idPrefix}uploaded-images-count`} sx={imageCountContainer}>
      <span sx={imageCountStyle}>{count}</span>
    </div>
  ) : null;
};

const Thumbnail = ({
  url,
  badge,
  attachments,
  index,
}: {
  url: string;
  badge: QualityBadge | null;
  attachments?: Array<Attachment>;
  index: number;
}): JSX.Element => {
  const dispatch = useAppDispatch();
  const handleOnClickThumbnail = (): void => {
    dispatch(
      showModal({
        type: ModalType.Fullscreen,
        variables: {
          attachments,
          index,
        },
      }),
    );
  };

  return (
    <div
      id="image-thumbnail"
      onClick={handleOnClickThumbnail}
      sx={thumbnailWrapperStyle}
    >
      <img src={url} alt="image" sx={thumbnailStyle} />
      {badge && (
        <span
          data-test-id="image-quality-badge"
          sx={badge === 'hidden' ? qualityBadgeHiddenStyle : qualityBadgeStyle}
        >
          {QUALITY_BADGE_LABEL[badge]}
        </span>
      )}
    </div>
  );
};

const ImageLoader = ({ idPrefix }: { idPrefix: string }): JSX.Element => (
  <div id={`${idPrefix}image-list-loader`} sx={imageListStyle}>
    <Skeleton sx={loaderStyles} />
  </div>
);

const ImageList = ({
  attachments,
  flexDirection,
  idPrefix,
}: {
  attachments?: Array<Attachment>;
  flexDirection: 'row' | 'column';
  idPrefix: string;
}): JSX.Element => (
  <div>
    {!attachments || attachments.length === 0 ? (
      <span sx={noImagesLabelStyle} id={`${idPrefix}no-images-label`}>
        No images uploaded
      </span>
    ) : (
      <div
        id={`${idPrefix}image-list`}
        sx={{ ...imageListStyle, flexDirection }}
      >
        {attachments.map((item, index) => {
          const { url, id } = item;
          return (
            <Thumbnail
              url={url}
              badge={qualityBadge(item)}
              key={id}
              attachments={attachments}
              index={index}
            />
          );
        })}
      </div>
    )}
  </div>
);
const CategorySection = ({
  title,
  attachments,
  isLoading,
  flexDirection,
  idPrefix,
}: {
  title: string;
  attachments: Array<Attachment>;
  isLoading?: boolean;
  flexDirection: 'row' | 'column';
  idPrefix: string;
}): JSX.Element => (
  <div sx={imageGalleryWrapper}>
    <div
      id={`${idPrefix}uploaded-images-title`}
      sx={uploadedImagesTitleWrapper}
    >
      <span sx={uploadedImagesTitle}>{title}</span>
      {attachments.length > 0 && (
        <ImageCount count={attachments.length} idPrefix={idPrefix} />
      )}
    </div>
    {isLoading ? (
      <ImageLoader idPrefix={idPrefix} />
    ) : (
      <ImageList
        attachments={attachments}
        flexDirection={flexDirection}
        idPrefix={idPrefix}
      />
    )}
  </div>
);

const ImageGallery = ({
  attachments,
  isLoading,
  flexDirection = 'row',
}: {
  attachments?: Array<Attachment>;
  isLoading?: boolean;
  flexDirection?: 'row' | 'column';
}): JSX.Element => {
  const deliveryAttachments = attachments
    ? attachments.filter(
        (attachment) => attachment.attachmentCategory === 'delivery',
      )
    : [];
  const skipScanAttachments = attachments
    ? attachments.filter(
        (attachment) => attachment.attachmentCategory === 'skip_scan',
      )
    : [];

  return (
    <>
      <CategorySection
        title="Delivery Photos"
        attachments={deliveryAttachments}
        isLoading={isLoading}
        flexDirection={flexDirection}
        idPrefix=""
      />
      <CategorySection
        title="Skip Scan Photos"
        attachments={skipScanAttachments}
        isLoading={isLoading}
        flexDirection={flexDirection}
        idPrefix="skip-scan-"
      />
    </>
  );
};

export default ImageGallery;
